import { Link } from "react-router-dom";
import { CheckCircle2, Wallet, Zap, Rocket } from "lucide-react";
import { Button } from "../ui/button";

const PLANS = [
  { key: "starter", icon: Wallet, price: 500, credits: 500, resumes: 250, jobs: 5, popular: false },
  { key: "growth", icon: Zap, price: 1000, credits: 1000, resumes: 500, jobs: 10, popular: true },
  { key: "scale", icon: Rocket, price: 1500, credits: 1500, resumes: 1000, jobs: 20, popular: false },
];

export default function LandingPricing() {
  return (
    <section id="pricing" className="relative overflow-hidden bg-muted/30 px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
      <div className="pointer-events-none absolute -bottom-24 left-1/2 h-[420px] w-[760px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,hsl(var(--primary)/0.12),transparent)] blur-2xl" />
      <div className="relative mx-auto max-w-6xl">
        <div className="mx-auto max-w-xl text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-primary">Simple credit-based pricing</span>
          <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Pay only for what you evaluate</h2>
          <p className="mt-3 text-sm text-muted-foreground sm:text-base">Credits never expire. Every resume evaluation costs 2 credits. New signups get 10 free credits.</p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-3" data-testid="landing-pricing-grid">
          {PLANS.map((p, idx) => (
            <div
              key={p.key}
              className={`relative rounded-2xl border p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg animate-in fade-in-0 slide-in-from-bottom-2 glossy-card ${
                p.popular ? "border-primary bg-card ring-2 ring-primary/30" : "border-border/70 bg-card"
              }`}
              style={{ animationDelay: `${idx * 90}ms` }}
              data-testid={`landing-pricing-card-${p.key}`}
            >
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                  Most Popular
                </span>
              )}
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15">
                <p.icon className="h-5 w-5 text-primary" />
              </div>
              <div className="mt-4 font-mono text-3xl font-bold">₹{p.price}</div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground capitalize">{p.key} pack</div>
              <ul className="mt-5 space-y-2.5 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> {p.credits} credits</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> ~{p.resumes} resume evaluations</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> +{p.jobs} job slots</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> Unlimited batches per job</li>
              </ul>
              <Button asChild className="mt-6 w-full" variant={p.popular ? "default" : "outline"} data-testid={`landing-pricing-cta-${p.key}`}>
                <Link to="/signup">Get Started</Link>
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
